import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Dropdown, DropDownProps, Tooltip } from 'antd';
import isEqual from 'react-fast-compare';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as FilterSvg } from 'views/Conversation/ConvList/assets/filter.svg';
import { getDefaultFilte } from '../hooks/useSearch';
import FilterForm from './FilterForm';

const Container = styled.div`
  display: flex;
  align-items: center;
  > span {
    cursor: pointer;
  }
`;

const Trigger = styled.div<{ active: string }>`
  margin-right: 4px;
  padding: 0 6px;
  color: ${({ active }) => (active === 'true' ? '#5C92FF' : 'var(--fett-text-secondary)')};
  background: ${({ active }) => (active === 'true' ? '#F0F7FF' : 'transparent')};
  [data-color-mode='dark'] & {
    color: ${({ active }) => (active === 'true' ? '#8aadff' : 'var(--fett-text-secondary)')};
    background: ${({ active }) => (active === 'true' ? 'rgba(51, 102, 255, 0.16)' : 'transparent')};
  }
  cursor: pointer;
  > .anticon {
    margin-right: 4px;
    font-size: 16px;
    color: currentColor;
  }
`;

const Overlay = styled.div`
  background: var(--fett-bg-panel);
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  padding-right: 16px;
  background: var(--fett-bg-panel);
  border-top: solid 1px var(--fett-border-default);
  box-shadow: 0px 2px 12px 0px rgba(167, 178, 204, 0.1);
`;
const ToolbarButton = styled(Button)`
  padding: 4px 8px;
`;
const ExportBtn = styled(Button)`
  margin-right: 8px;
`;

const getFilteCount = (filte: Review.Filte) => {
  const {
    platform,
    region,
    follower,
    storeId,
    reviewStartTime,
    reviewEndTime,
    reviewRating,
    productRating,
    sellerRating,
    logisticsRating,
    handleStatus,
    hasContent,
    spuName,
  } = filte;

  let res = 0;

  if (region || follower === '1' || (storeId && storeId.length)) {
    res += 1;
  }

  if (reviewStartTime && reviewEndTime) {
    res += 1;
  }

  if (reviewRating && reviewRating.length) {
    res += 1;
  }

  if (platform === 'lazada') {
    if (productRating && productRating.length) {
      res += 1;
    }

    if (sellerRating && sellerRating.length) {
      res += 1;
    }

    if (logisticsRating && logisticsRating.length) {
      res += 1;
    }
  }

  if (handleStatus && handleStatus.length) {
    res += 1;
  }

  if (hasContent !== 'all') {
    res += 1;
  }

  if (spuName !== '') {
    res += 1;
  }

  return res;
};

export const isDefaultFilte = (filte: Review.Filte) => {
  const { platform } = filte;

  const defaultFilte = getDefaultFilte(platform);

  return isEqual(filte, defaultFilte);
};

const getEmptyFilte = (filte: Review.Filte): Review.Filte => {
  const { platform } = filte;

  return {
    platform,
    hasContent: 'all',
  };
};

export interface NewFilterProps extends Pick<DropDownProps, 'visible' | 'onVisibleChange'> {
  filte: Review.Filte;
  onFilteChange: (filte: Review.Filte) => void;
  onExport: (filte: Review.Filte) => void;
}

function NewFilter(props: NewFilterProps) {
  const { filte, onFilteChange, visible, onVisibleChange, onExport } = props;

  const { t } = useTranslate();

  const filteCount = useMemo(() => getFilteCount(filte), [filte]);

  const overlay = (
    <FilterForm
      initialValues={filte}
      onSubmit={(values, helpers) => {
        onFilteChange(values);

        helpers.resetForm();
      }}
    >
      {(form, formProps) => {
        const { values, dirty, setValues, handleSubmit } = formProps;

        const defaultFilte = getDefaultFilte(values.platform);

        const emptyFilte = getEmptyFilte(values);

        return (
          <Overlay>
            {form}
            <Toolbar>
              <div>
                <ToolbarButton
                  type="link"
                  disabled={isEqual(values, defaultFilte)}
                  onClick={() => setValues(defaultFilte)}
                >
                  {t('scene.reviewWorkspace.list.action.restoreDefault')}
                </ToolbarButton>
                <ToolbarButton
                  type="link"
                  disabled={isEqual(values, emptyFilte)}
                  onClick={() => setValues(emptyFilte)}
                >
                  {t('scene.reviewWorkspace.list.action.clearSelection')}
                </ToolbarButton>
              </div>
              <div>
                <ExportBtn
                  size="small"
                  onClick={() => {
                    onExport(values);
                  }}
                >
                  {t('scene.reviewWorkspace.list.action.export')}
                </ExportBtn>
                <Button
                  type="primary"
                  size="small"
                  disabled={!dirty}
                  onClick={() => {
                    handleSubmit();

                    if (onVisibleChange) {
                      onVisibleChange(false);
                    }
                  }}
                >
                  {t('scene.reviewWorkspace.list.action.search')}
                </Button>
              </div>
            </Toolbar>
          </Overlay>
        );
      }}
    </FilterForm>
  );

  return (
    <Container>
      <Dropdown
        trigger={['click']}
        placement="bottomLeft"
        visible={visible}
        onVisibleChange={onVisibleChange}
        overlay={overlay}
      >
        {filteCount > 0 ? (
          <Tooltip title={t('scene.reviewWorkspace.list.action.filter')}>
            <Trigger active="true">
              <Icon component={FilterSvg} />
              {filteCount}
            </Trigger>
          </Tooltip>
        ) : (
          <Trigger active="false">
            <Icon component={FilterSvg} />
            {t('scene.reviewWorkspace.list.action.filter')}
          </Trigger>
        )}
      </Dropdown>
      <Tooltip
        arrowPointAtCenter
        placement="topLeft"
        title={
          <>
            <div>{t('scene.reviewWorkspace.list.filter.hint.priorityTip')}</div>
            <div>{t('scene.reviewWorkspace.list.filter.hint.customizable')}</div>
          </>
        }
      >
        <InfoCircleOutlined />
      </Tooltip>
    </Container>
  );
}

export default NewFilter;
